// Presentation helpers for the ticket drawer ("פניות לקלאוד").

export const TITLE_MAX = 60

/** Ticket title: server sends ≤60 chars; trim defensively, never more than 60. */
export const ticketTitle = text => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim()
  return clean.length > TITLE_MAX ? clean.substr(0, TITLE_MAX).trim() + '…' : clean
}

const pad = n => (n < 10 ? '0' : '') + n

/** "לפני 5 דק׳" style relative time. `now` is injectable for tests. */
export const relativeTime = (iso, now) => {
  const t = iso ? Date.parse(iso) : NaN
  if (isNaN(t)) return ''
  const nowMs = typeof now === 'number' ? now : Date.now()
  const minutes = Math.floor(Math.max(0, nowMs - t) / 60000)
  if (minutes < 1) return 'עכשיו'
  if (minutes < 60) return 'לפני ' + minutes + ' דק׳'
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return hours === 1 ? 'לפני שעה' : 'לפני ' + hours + ' שע׳'
  const days = Math.floor(hours / 24)
  if (days === 1) return 'אתמול'
  if (days < 7) return 'לפני ' + days + ' ימים'
  const d = new Date(t)
  return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear()
}

/** Short display form of a linked Jira task id: numeric -> "#123", else as-is. */
export const jiraShortId = id => {
  const s = String(id === undefined || id === null ? '' : id).trim()
  return /^\d+$/.test(s) ? '#' + s : s
}

/** The follow-up text sent by "בדקי סטטוס". */
export const jiraStatusQuestion = id => 'מה הסטטוס של המשימה ' + jiraShortId(id) + '?'

/** Recruiter filter option for the supervisor view: "רונית לוי (3 פתוחות)". */
export const adminOptionLabel = admin => {
  if (!admin) return ''
  const name = String(admin.name || '').trim() || ('#' + admin.id)
  const open = parseInt(admin.open_count, 10) || 0
  return open > 0 ? name + ' (' + open + ' פתוחות)' : name
}

/** Absolute timestamp "07/10/2026 13:05" (local time); '' for missing/invalid. */
export const dateTime = iso => {
  const t = iso ? Date.parse(iso) : NaN
  if (isNaN(t)) return ''
  const d = new Date(t)
  return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear() + ' ' +
    pad(d.getHours()) + ':' + pad(d.getMinutes())
}

// ---- Supervisor intervention (SUPERVISOR_SPEC "UI and Hebrew copy";
// INSTRUCTION_SPEC (d)/(f): management is never visible to recruiters) ----

export const MANAGEMENT_TAG = 'הנהלה'
export const INSTRUCTION_TAG = 'הנחיית הנהלה — פנימי'
export const RE_ANSWER_LABEL = 'תשובה מעודכנת'
export const ANSWER_REFRESH_TEXT = 'המערכת מכינה תשובה מעודכנת…'
export const CANCELLED_BY_SUPERVISOR_TEXT = 'המענה לשאלה זו בוטל על ידי ההנהלה'
// Recruiter copy: no management attribution.
export const RECRUITER_CANCELLED_TEXT = 'המענה לשאלה זו בוטל.'
export const CANCELLED_ERROR_CODE = 'cancelled_by_supervisor'
export const SUPERSEDED_ERROR_CODE = 'superseded_by_instruction'

export const SYSTEM_NOTE_TAG = 'הודעת מערכת'
export const KIND_SYSTEM_NOTE = 'system_note'
export const KIND_SUPERVISOR_NOTE = 'supervisor_note'
export const KIND_SUPERVISOR_INSTRUCTION = 'supervisor_instruction'
// The system's private answer to a supervisor instruction (supervisor view only).
export const KIND_SUPERVISOR_REPLY = 'supervisor_reply'
export const SUPERVISOR_REPLY_TAG = 'תשובת המערכת להנהלה — פנימי'
// Management-only rows: never rendered (nor kept) on the recruiter side.
export const PRIVATE_KINDS = [KIND_SUPERVISOR_NOTE, KIND_SUPERVISOR_INSTRUCTION, KIND_SUPERVISOR_REPLY]

/** Server-resolved display name of an {id, name} actor; '' when unavailable. */
export const actorName = actor => (actor && typeof actor === 'object' ? String(actor.name || '').trim() : '')

/** Row is a historical management message (kind=supervisor_note), not a question turn. */
export const isSupervisorNote = q => !!q && q.kind === KIND_SUPERVISOR_NOTE

/** Row is a private supervisor instruction (kind=supervisor_instruction). */
export const isSupervisorInstruction = q => !!q && q.kind === KIND_SUPERVISOR_INSTRUCTION

/** Row is the system's private reply to a supervisor instruction (kind=supervisor_reply). */
export const isSupervisorReply = q => !!q && q.kind === KIND_SUPERVISOR_REPLY

/** Public short system message (kind=system_note): answer-only, no thumbs, no author. */
export const isSystemNote = q => !!q && q.kind === KIND_SYSTEM_NOTE

/** Any management-only row (note or instruction). */
export const isPrivateRow = q => !!q && PRIVATE_KINDS.indexOf(q.kind) !== -1

/**
 * Original question id a re-answer job belongs to, or null. Supervisor rows carry
 * context._qa_internal.re_answer_of; recruiter rows may carry a top-level
 * re_answer_of (the internal metadata is never exposed to recruiters).
 */
export const reAnswerOf = q => {
  if (!q) return null
  const internal = q.context && q.context._qa_internal
  const id = parseInt(q.re_answer_of || (internal && internal.re_answer_of), 10)
  return id > 0 ? id : null
}

export const isReAnswer = q => reAnswerOf(q) !== null

/** A question the recruiter actually asked (not a note, instruction or re-answer job). */
export const isGenuineQuestion = q => !!q && (q.kind || 'question') === 'question' && !isReAnswer(q)

/** Newest genuine recruiter question of a thread (by id), or null. */
export const latestGenuineQuestion = rows => (rows || []).reduce((best, q) =>
  (isGenuineQuestion(q) && (!best || q.id > best.id)) ? q : best, null)

/**
 * Display order: each genuine turn followed by its re-answer jobs (by id);
 * notes / instructions / orphan re-answers keep their id position.
 */
export const groupTurns = rows => {
  const list = (rows || []).filter(Boolean).slice().sort((a, b) => a.id - b.id)
  const present = {}
  list.forEach(q => { if (!isReAnswer(q)) present[q.id] = true })
  const children = {}
  list.forEach(q => {
    const of = reAnswerOf(q)
    if (of && present[of]) (children[of] = children[of] || []).push(q)
  })
  const out = []
  list.forEach(q => {
    const of = reAnswerOf(q)
    if (of && present[of]) return
    out.push(q)
    ;(children[q.id] || []).forEach(c => out.push(c))
  })
  return out
}

/**
 * Recruiter projection of a row: null for management-only kinds; management
 * attribution (answer_author / answer_action / author) and internal context
 * stripped. Defensive: the server already does this, but an old cached payload
 * must never render it.
 */
export const sanitizeRecruiterRow = q => {
  if (!q || isPrivateRow(q)) return null
  const out = { ...q }
  delete out.answer_author
  delete out.answer_action
  delete out.author
  if (out.context && typeof out.context === 'object' && out.context._qa_internal) {
    const internal = out.context._qa_internal
    if (internal && internal.re_answer_of && !out.re_answer_of) out.re_answer_of = internal.re_answer_of
    out.context = { ...out.context }
    delete out.context._qa_internal
  }
  return out
}

/** Answer approved or supplied by a supervisor (not the worker). Supervisor view only. */
export const isSupervisorAnswered = q => !!q && !isPrivateRow(q) &&
  (q.answer_action === 'approve' || q.answer_action === 'answer' || !!(q.answer_author && q.answer_author.id))

// Recruiter projection normalises the cancel code to 'cancelled'.
export const PUBLIC_CANCELLED_ERROR_CODE = 'cancelled'

/** Turn cancelled by management: failed + cancelled_by_supervisor (supervisor view) or 'cancelled' (recruiter view). */
export const isCancelledBySupervisor = q => !!q && q.status === 'failed' &&
  (q.error_code === CANCELLED_ERROR_CODE || q.error_code === PUBLIC_CANCELLED_ERROR_CODE)

/**
 * Recruiter display status of a turn. A genuine question whose run was superseded
 * by an instruction (status 'superseded'):
 *   - its re-answer is queued/running (answer_refresh) -> 'running' ("בטיפול")
 *   - a published re-answer exists under it            -> '' (no pill: the re-answer carries it)
 *   - neither                                           -> 'failed' (normal retry)
 */
export const recruiterTurnStatus = (q, { refreshPending = false, hasPublishedReAnswer = false } = {}) => {
  if (!q) return ''
  if (q.status !== 'superseded') return q.status
  if (refreshPending) return 'running'
  if (hasPublishedReAnswer) return ''
  return 'failed'
}

/** Recruiter ticket-level status ('superseded' resolved with the pending refresh only). */
export const recruiterTicketStatus = (status, refreshPending) =>
  status === 'superseded' ? (refreshPending ? 'running' : 'failed') : (status || '')

/** Re-answer job retired because a newer instruction / follow-up replaced it. */
export const isSuperseded = q => !!q && q.status === 'failed' && q.error_code === SUPERSEDED_ERROR_CODE

/** Supervisor view: "אושר על ידי הראל" / "נענה על ידי הראל"; '' for worker answers. */
export const answerAttribution = q => {
  if (!isSupervisorAnswered(q)) return ''
  const who = actorName(q.answer_author) || 'ההנהלה'
  return (q.answer_action === 'approve' ? 'אושר על ידי ' : 'נענה על ידי ') + who
}

/** Management-bubble header: "הנהלה · הראל" (tag alone without a name). */
export const noteAuthorLine = q => {
  const who = actorName(q && q.author)
  return who ? MANAGEMENT_TAG + ' · ' + who : MANAGEMENT_TAG
}

/** Thumbs on every settled public answer, including manual / approved ones. */
export const canGiveFeedback = q => !!q && !isPrivateRow(q) && !isSystemNote(q) &&
  ['answered', 'needs_input', 'needs_human'].indexOf(q.status) !== -1

/** Generic "נסי שוב" only for a worker failure of a genuine question. */
export const canRetryTurn = q => !!q && isGenuineQuestion(q) && q.status === 'failed' && !isCancelledBySupervisor(q)

// Answer kinds (management ones are normalised server-side to the plain answer
// kind; an old cached supervisor_answer / supervisor_approval still toasts with
// the ordinary system wording). supervisor_message is a hidden note: no toast.
const TOASTABLE_KINDS = ['worker_answer', 'supervisor_answer', 'supervisor_approval', 'system_note']

/** last_notification kinds that produce a toast (failures / cancels only refresh). */
export const isToastableNotification = last => !!last && TOASTABLE_KINDS.indexOf(last.kind) !== -1
